import { Button, DatePicker, Space, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useMemo, useState, useEffect } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { tableFromIPC } from "@uwdata/flechette";
import dayjs, { Dayjs } from "dayjs";
import {
  getEnergyScheduleOptions,
  getMeasurementsForSiteInArrowOptions,
} from "../../../services/client/@tanstack/react-query.gen";
import {
  ChargingProfile,
  Profile,
  SiteReadable,
  SiteSchedule,
} from "../../../services/client";
import { parseArrowToTimeSeries } from "../Components/dataUtils";
import {
  ScheduleVsMeasurementChart,
  SchedulePoint,
  WindowReadyEvent,
} from "./ScheduleVsMeasurementChart";
import {
  DATE_TIME_FORMAT,
  formatDateTime,
  TIME_FORMAT,
} from "../../../util/formatDate";

const { Text } = Typography;

const FIFTEEN_MIN_MS = 15 * 60 * 1000;
const WINDOW_HOURS = 4;
const WINDOW_MS = WINDOW_HOURS * 60 * 60 * 1000;
// Extra past quarter-hours to query so we can fall back to an older schedule
// when the schedule intended for a window wasn't finished optimizing in time.
const LOOKBACK_WINDOWS = 4;

const floorToQuarterHour = (d: Date): Date =>
  new Date(Math.floor(d.getTime() / FIFTEEN_MIN_MS) * FIFTEEN_MIN_MS);

type ScheduleFollowingTabProps = {
  site: SiteReadable;
};

export const ScheduleFollowingTab: React.FC<ScheduleFollowingTabProps> = ({
  site,
}) => {
  const [windowStart, setWindowStart] = useState<Date>(() =>
    floorToQuarterHour(new Date(Date.now() - WINDOW_MS)),
  );
  const windowEnd = useMemo(
    () => new Date(windowStart.getTime() + WINDOW_MS),
    [windowStart],
  );

  const dispatchableAssets = useMemo(() => {
    return (
      site.configurations?.site_configuration?.device_list?.filter(
        (d) =>
          d.type === "charging_pole_3phase" ||
          d.type === "dc_charging_pole" ||
          d.type === "battery_energy_storage_system",
      ) ?? []
    );
  }, [site]);

  const assetNames = useMemo(
    () => dispatchableAssets.map((d) => d.name),
    [dispatchableAssets],
  );

  const windowStarts = useMemo(() => {
    const starts: Date[] = [];
    for (
      let t = windowStart.getTime();
      t < windowEnd.getTime();
      t += FIFTEEN_MIN_MS
    ) {
      starts.push(new Date(t));
    }
    return starts;
  }, [windowStart, windowEnd]);

  // Query a few quarter-hours before the displayed window too, so we have
  // older schedules in hand to fall back to when an optimization wasn't
  // finished by the time its target window started.
  const queryAtTimes = useMemo(() => {
    const times: Date[] = [];
    for (let i = -LOOKBACK_WINDOWS; i < windowStarts.length; i++) {
      times.push(new Date(windowStart.getTime() + i * FIFTEEN_MIN_MS));
    }
    return times;
  }, [windowStart, windowStarts]);

  const scheduleQueries = useQueries({
    queries: queryAtTimes.map((at) => ({
      ...getEnergyScheduleOptions({
        path: { site_id: site.site_id, at_time: at.toISOString() },
      }),
      retry: false,
      staleTime: 60_000,
    })),
  });

  const measurementsQuery = useQuery({
    ...getMeasurementsForSiteInArrowOptions({
      path: { site_id: site.site_id },
      body: assetNames,
      query: {
        start: windowStart.toISOString(),
        stop: windowEnd.toISOString(),
      },
    }),
    enabled: assetNames.length > 0,
  });

  const [measurementsByAsset, setMeasurementsByAsset] = useState<
    Record<string, { timestamps: Date[]; values: (number | null)[] }>
  >({});

  useEffect(() => {
    const blob = measurementsQuery.data as Blob | undefined;
    if (!blob) {
      setMeasurementsByAsset({});
      return;
    }
    let cancelled = false;
    (async () => {
      const buffer = await blob.arrayBuffer();
      const table = tableFromIPC(buffer, { useProxy: true }).toArray();
      const series = parseArrowToTimeSeries(table);
      if (cancelled) {
        return;
      }
      const byAsset: Record<
        string,
        { timestamps: Date[]; values: (number | null)[] }
      > = {};
      for (const s of series) {
        byAsset[s.name] = { timestamps: s.timestamps, values: s.values };
      }
      setMeasurementsByAsset(byAsset);
    })();
    return () => {
      cancelled = true;
    };
  }, [measurementsQuery.data]);

  const { schedulePointsByAsset, scheduleEvents, windowReadyEvents } =
    useMemo(() => {
      const result: Record<string, SchedulePoint[]> = {};
      for (const name of assetNames) {
        result[name] = windowStarts.map((ws) => ({
          windowStart: ws,
          setpoint: null,
        }));
      }

      // Deduplicate schedules across all queries — an at_time query may return
      // a schedule whose timestamp is older than the requested time, leading
      // to repeats across consecutive queries.
      const scheduleLibrary: Array<{
        schedule: SiteSchedule;
        timestampMs: number;
        // When the optimization actually finished. Falls back to the schedule
        // timestamp when missing so older schedules still count as available.
        readyAtMs: number;
      }> = [];
      const seen = new Set<string>();
      for (const q of scheduleQueries) {
        const schedule = q.data as SiteSchedule | undefined;
        if (!schedule?.schedules?.length) {
          continue;
        }
        const key = schedule._id ?? schedule.timestamp;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        const timestampMs = new Date(schedule.timestamp).getTime();
        const readyAtMs = schedule.created_at
          ? new Date(schedule.created_at).getTime()
          : timestampMs;
        scheduleLibrary.push({ schedule, timestampMs, readyAtMs });
      }

      // Active schedule at a given time: the one whose optimization finished
      // most recently before that time. Tie-break by intended timestamp so a
      // later-targeted schedule wins.
      const findActiveSchedule = (atMs: number) => {
        let active: (typeof scheduleLibrary)[number] | null = null;
        for (const candidate of scheduleLibrary) {
          if (candidate.readyAtMs > atMs) {
            continue;
          }
          if (candidate.timestampMs > atMs) {
            continue;
          }
          if (
            !active ||
            candidate.readyAtMs > active.readyAtMs ||
            (candidate.readyAtMs === active.readyAtMs &&
              candidate.timestampMs > active.timestampMs)
          ) {
            active = candidate;
          }
        }
        return active;
      };

      // Pick the profile entry inside [atMs, atMs + 15min), otherwise the
      // most recent entry at or before atMs (so older schedules still expose
      // a setpoint for the requested window).
      const pickSetpoint = (profile: Profile[], atMs: number) => {
        const atEndMs = atMs + FIFTEEN_MIN_MS;
        let chosen: number | null = null;
        let chosenTs = -Infinity;
        const chargingProfile = profile.filter(
          (point): point is ChargingProfile => point.type !== "grid",
        );
        for (const point of chargingProfile) {
          const pMs = new Date(point.timestamp).getTime();
          if (pMs >= atMs && pMs < atEndMs) {
            return point.powerValue;
          }
          if (pMs <= atMs && pMs > chosenTs) {
            chosen = point.powerValue;
            chosenTs = pMs;
          }
        }
        return chosen;
      };

      // One entry per distinct active schedule encountered across the visible
      // quarter-hours, marking when it actually became ready (falling back to
      // its own intended timestamp when `created_at` is missing).
      const readyEvents: WindowReadyEvent[] = [];
      let lastReadyAtMs: number | null = null;

      for (let wi = 0; wi < windowStarts.length; wi++) {
        const ws = windowStarts[wi];
        const wsMs = ws.getTime();

        const active = findActiveSchedule(wsMs);
        if (!active) {
          continue;
        }

        for (const assetSchedule of active.schedule.schedules) {
          const name = assetSchedule.assetId;
          if (!(name in result)) {
            continue;
          }
          result[name][wi] = {
            windowStart: ws,
            setpoint: pickSetpoint(assetSchedule.profile, wsMs),
          };
        }

        if (active.readyAtMs !== lastReadyAtMs) {
          readyEvents.push({
            readyAt: new Date(active.readyAtMs),
            hasCreatedAt: Boolean(active.schedule.created_at),
          });
          lastReadyAtMs = active.readyAtMs;
        }
      }
      return {
        schedulePointsByAsset: result,
        // One vertical line per schedule object returned by the queries,
        // placed at that schedule's own `timestamp` field.
        scheduleEvents: scheduleLibrary
          .filter(
            ({ timestampMs }) =>
              timestampMs >= windowStart.getTime() &&
              timestampMs <= windowEnd.getTime(),
          )
          .map(({ schedule, timestampMs }) => ({
            timestamp: new Date(timestampMs),
            createdAt: schedule.created_at
              ? new Date(schedule.created_at)
              : undefined,
          })),
        windowReadyEvents: readyEvents,
      };
    }, [scheduleQueries, windowStarts, assetNames, windowStart, windowEnd]);

  const schedulesLoading = scheduleQueries.some((q) => q.isLoading);
  const scheduleError = scheduleQueries.find((q) => q.error)?.error as
    | Error
    | undefined;
  const measurementsError = measurementsQuery.error as Error | undefined;

  const shiftWindow = (deltaMs: number) =>
    setWindowStart((prev) => new Date(prev.getTime() + deltaMs));

  const handlePickerChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }
    setWindowStart(floorToQuarterHour(value.toDate()));
  };

  return (
    <div className="w-full">
      <Space className="mb-4" wrap>
        <Button icon={<LeftOutlined />} onClick={() => shiftWindow(-WINDOW_MS)}>
          Previous {WINDOW_HOURS}h
        </Button>
        <DatePicker
          showTime={{ minuteStep: 15, format: TIME_FORMAT }}
          format={DATE_TIME_FORMAT}
          value={dayjs(windowStart)}
          onChange={handlePickerChange}
          allowClear={false}
        />
        <Button onClick={() => shiftWindow(WINDOW_MS)}>
          Next {WINDOW_HOURS}h <RightOutlined />
        </Button>
        <Text type="secondary">
          {formatDateTime(windowStart)} → {formatDateTime(windowEnd)}
        </Text>
      </Space>

      {assetNames.length === 0 ? (
        <Text type="secondary">
          No dispatchable assets (charging poles or BESS) configured for this
          site.
        </Text>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {assetNames.map((name) => (
            <ScheduleVsMeasurementChart
              key={name}
              assetName={name}
              schedulePoints={schedulePointsByAsset[name] ?? []}
              windowReadyEvents={windowReadyEvents}
              measurementTimestamps={
                measurementsByAsset[name]?.timestamps ?? []
              }
              measurementValues={measurementsByAsset[name]?.values ?? []}
              scheduleEvents={scheduleEvents}
              windowStart={windowStart}
              windowEnd={windowEnd}
              isLoading={schedulesLoading || measurementsQuery.isLoading}
              error={scheduleError ?? measurementsError ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
};
